import { Controller } from "stimulus"
import { localStorageAvailable } from "../utils"

function observe(elements, options, handler) {
  const observers = elements.map(el => {
    const observer = new MutationObserver(mutations => {
      mutations.forEach(handler)
    })
    observer.observe(el, options)

    return observer
  })

  return {
    disconnect: () => observers.forEach(o => o.disconnect())
  }
}

function isVisible(el) {
  return el && getComputedStyle(el).display != "none"
}

export default class extends Controller {
  static targets = [ "tab", "content" ]
  static classes = [ "active" ]

  connect() {
    this._observer = observe(
      this.tabTargets,
      {
        attributes: true,
        attributeFilter: [ "style" ]
      },
      mutation => {
        if (mutation.type == "attributes") {
          this.toggleTab(mutation)
        }
      }
    )

    this.restoreTab()

    // activate first tab if non active
    if (!isVisible(this.activeTab)) {
      this.activateTab(this.visibleTabs[0])
    }
  }

  tabTargetConnected(el) {
    el.addEventListener("click", this.tabClicked)
  }

  tabTargetDisconnected(el) {
    el.removeEventListener("click", this.tabClicked)
  }

  disconnect() {
    this._observer?.disconnect()
  }

  tabClicked = ({ currentTarget: el }) => {
    this.activateTab(el)
  }

  activateTab(tab) {
    if (!this.visibleTabs.includes(tab)) { return }

    this.tabTargets.forEach(el => el.classList.remove(this.activeClass))
    tab.classList.add(this.activeClass)

    const tabIndex = this.tabTargets.indexOf(tab)

    this.contentTargets.forEach((content, index) => {
      // support multiple tab contents per tab via data-tabs-index=<number>
      // attribute on tab content elements
      if (undefined !== content.dataset.tabsIndex) {
        content.hidden = tabIndex != parseInt(content.dataset.tabsIndex, 10)
      } else {
        content.hidden = tabIndex != index
      }

      content.querySelectorAll(".tab-toggle-disabled").forEach(el => {
        el.disabled = content.hidden
      })
    })

    this.rememberTab(tab)
  }

  // called from mutation observer
  // activates first tab if there none other active
  toggleTab({ target: tab }) {
    if (isVisible(tab)) {
      this.activateTab(tab)
      return
    }

    const activeTab = this.visibleTabs.some(el => el.classList.contains(this.activeClass))
    if (!activeTab) {
      this.activateTab(this.visibleTabs[0])
    }
  }

  get activeTab() {
    return this.visibleTabs.find(el => el.classList.contains(this.activeClass))
  }

  get visibleTabs() {
    return this.tabTargets.filter(isVisible)
  }

  rememberTab(tab) {
    if (this.#tabMemoryEnabled) {
      localStorage.setItem(this.tabStorageKey, this.tabRestoreId(tab))
    }
  }

  restoreTab() {
    if (this.#tabMemoryEnabled) {
      const isRemembered = el => {
        return this.tabRestoreId(el) === localStorage.getItem(this.tabStorageKey)
      }
      this.activateTab(this.visibleTabs.find(isRemembered))
    }
  }

  tabRestoreId(el) {
    return el.dataset.tabsRestoreId || String(this.tabTargets.indexOf(el))
  }

  get #tabMemoryEnabled() {
    return this.element.dataset.tabsRestoreKey && localStorageAvailable()
  }

  get tabStorageKey() {
    return `${this.element.dataset.tabsRestoreKey}-${this.identifier}`
  }
}
